import { useEffect, useState } from 'react';
import { useTranslation } from '../i18n/LocalizationProvider';
import type { TranslationContent } from '../i18n/translations';

interface DashboardData {
  couponsCount: number;
  couponsValue: number;
  resultsCount: number;
  pricingTier: string;
}

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState(false);
  const { t } = useTranslation();
  const metrics = t<TranslationContent['dashboard']['metrics']>('dashboard.metrics');
  const checklistItems = t<string[]>('dashboard.checklist.items');

  useEffect(() => {
    let mounted = true;
    setTimeout(() => {
      if (!mounted) return;
      try {
        setData({
          couponsCount: 5,
          couponsValue: 150,
          resultsCount: 12,
          pricingTier: 'Pro',
        });
      } catch (err) {
        console.error(err);
        setError(true);
      }
    }, 500);

    return () => {
      mounted = false;
    };
  }, []);

  if (error) {
    return (
      <div className="panel">
        <p className="text-muted" style={{ color: '#c13a3a' }}>{t('dashboard.error')}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="panel" style={{ textAlign: 'center' }}>
        <p className="text-muted">{t('dashboard.loading')}</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="section-heading">
        <div>
          <span className="badge">{t('dashboard.badge')}</span>
          <h2>{t('dashboard.title')}</h2>
          <p className="text-muted">{t('dashboard.description')}</p>
        </div>
        <div className="tag-pill">{t('dashboard.tierLabel', { tier: data.pricingTier })}</div>
      </div>

      <div className="metric-grid" style={{ marginBottom: '2rem' }}>
        <div className="metric-card">
          <span>{metrics.couponsIssued}</span>
          <strong>{data.couponsCount}</strong>
        </div>
        <div className="metric-card">
          <span>{metrics.couponValue}</span>
          <strong>${data.couponsValue}</strong>
        </div>
        <div className="metric-card">
          <span>{metrics.resultsDelivered}</span>
          <strong>{data.resultsCount}</strong>
        </div>
        <div className="metric-card">
          <span>{metrics.testsRemaining}</span>
          <strong>{Math.max(0, 120 - data.resultsCount)}</strong>
        </div>
      </div>

      <div className="panel" style={{ boxShadow: 'none', background: 'transparent' }}>
        <h3>{t('dashboard.checklist.title')}</h3>
        <ul className="text-muted">
          {checklistItems.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
